import type { OneOfChildElements } from '@shared/models/project.model';
import type { OneOfChildFactoryElements } from '@shared/models/factory-element.model';
import { guards } from './typeguards.helpers';

/** Provenance that belongs to the source element, never to a copy of it. */
const PROVENANCE_KEYS = [
  'origin',
  'createdInProposalId',
  'importReplaceParent',
] as const;

/**
 * Recursive worker for {@link asManualCopy}. Untyped on purpose: it walks
 * stored elements and factory partials alike and only touches the provenance
 * keys and `elements`, so no element type is assumed.
 */
const stripProvenance = (node: unknown): unknown => {
  if (!guards.is.object(node)) return node;

  let changed = false;
  const children = guards.is.array(node.elements)
    ? node.elements.map((child) => {
        const stripped = stripProvenance(child);
        if (stripped !== child) changed = true;
        return stripped;
      })
    : undefined;

  const hasProvenance = PROVENANCE_KEYS.some((key) => key in node);
  if (!hasProvenance && !changed) return node;

  const {
    origin: _origin,
    createdInProposalId: _owner,
    importReplaceParent: _importParent,
    ...rest
  } = node;
  return { ...rest, ...(children ? { elements: children } : {}) };
};

/**
 * Make an element subtree a manual copy (DEV-3702): drop the provenance that
 * only the source may carry —
 *
 * - {@link IElement.origin}: exactly one element may claim a CSV/IFC candidate
 *   row, or the Imports tab, discard/restore and removal bookkeeping conflate
 *   the two;
 * - {@link IElement.createdInProposalId}: a copy made in baseline is baseline
 *   data, not owned by the proposal that created its source, so a delete in
 *   that proposal must soft-delete it like any other baseline element. A
 *   proposal-scoped add re-stamps the copy it creates.
 * - {@link IElement.importReplaceParent}: only the import-created parent is the
 *   grouping parent for its SBEF code. A duplicate is an ordinary element.
 *
 * Works on stored elements and factory partials alike; returns the same
 * reference when nothing in the subtree carries provenance.
 */
export const asManualCopy = <
  T extends OneOfChildElements | OneOfChildFactoryElements,
>(
  element: T,
): T => stripProvenance(element) as T;
